import { ArrowRightOutlined, MailOutlined } from '@ant-design/icons';
import { Button, Typography } from 'antd';
import classNames from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Notification } from 'types/Notification';
import { buildNotificationDisplayData } from './notificationDisplay';

interface NotificationListItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
  className?: string;
  showDescription?: boolean;
  onMarkAsRead?: (notificationId: string) => void;
}

const NotificationListItem: React.FC<NotificationListItemProps> = ({
  notification,
  onClick,
  className,
  showDescription = false,
  onMarkAsRead,
}) => {
  const { t } = useTranslation();
  const display = buildNotificationDisplayData(notification, t);
  const Icon = display.icon.Icon;

  const handleLinkClick = () => {
    onClick(notification);
  };

  const shouldShowMailMarkAction = display.isUnread && !!onMarkAsRead;

  const unreadIndicator = () => {
    if (!display.isUnread) {
      return null;
    }

    return (
      <span
        className={classNames(
          'inline-block h-1.5 w-1.5 rounded-full bg-bleu-hellio-6',
          shouldShowMailMarkAction &&
            'transition-opacity group-hover:opacity-0 group-focus-within:opacity-0',
        )}
      />
    );
  };

  const content = (
    <div className="flex items-start gap-2">
      <div className="pt-1">
        <span
          className={classNames(
            'inline-flex h-5 w-5 items-center justify-center rounded-sm p-0.5',
            display.icon.containerClassName,
          )}
        >
          <Icon className={classNames('text-xs', display.icon.iconClassName)} />
        </span>
      </div>

      <div className="min-w-0 grow">
        <div className="flex items-start justify-between gap-2">
          <Typography.Paragraph className="!mb-0 font-fieldwork text-sm leading-6 text-gris-hellio font-semibold max-w-64">
            {display.title}
          </Typography.Paragraph>
          <div className="flex items-center gap-2 pt-0.5">
            <Typography.Text className="shrink-0 whitespace-nowrap !break-normal text-xs leading-4 text-gris-clair-hellio">
              {display.dateLabel}
            </Typography.Text>
            {unreadIndicator()}
          </div>
        </div>

        {showDescription && (
          <Typography.Paragraph className="!mb-0 text-sm leading-6 text-gris-clair-hellio">
            {display.description}
          </Typography.Paragraph>
        )}

        <Typography.Paragraph className="!mb-0 mt-0.5 flex items-center gap-1 text-xs leading-5 text-bleu-hellio-6">
          <span className="truncate">{display.location}</span>
          <ArrowRightOutlined className="text-xs" />
        </Typography.Paragraph>
      </div>
    </div>
  );

  return (
    <div className={classNames('group relative', className)}>
      <Link
        to={display.path}
        tabIndex={0}
        className="block"
        onClick={handleLinkClick}
      >
        {content}
      </Link>

      {shouldShowMailMarkAction && (
        <Button
          type="text"
          icon={<MailOutlined className="text-xs" />}
          className="pointer-events-none absolute right-2 top-3 z-10 flex !h-4 !w-4 min-w-0 !items-center !justify-center !p-0 !text-bleu-hellio-6 !opacity-0 !transition-opacity group-hover:!pointer-events-auto group-hover:!opacity-100 group-focus-within:!pointer-events-auto group-focus-within:!opacity-100"
          aria-label={t('notification:mark_one_as_read')}
          title={t('notification:mark_one_as_read')}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onMarkAsRead(notification.id);
          }}
        />
      )}
    </div>
  );
};

export default NotificationListItem;
